import { useState, useCallback, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const EMPTY = { full_name: "", phone: "", email: "", consent: false, website: "" };

export default function SampleReportDialog({ open, onClose, onQuote }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError("");
    setForm(EMPTY);
  }, [open]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e)); };
  const handleClose = useCallback(() => { onClose(); setTimeout(() => setDone(false), 300); }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    document.body.style.overflow = "hidden";
    document.body.setAttribute("data-modal-open", "1");
    setTimeout(() => panelRef.current?.querySelector("input, select, button")?.focus(), 40);
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) { handleClose(); return; }
      if (e.key !== "Tab") return;
      const nodes = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!nodes || !nodes.length) return;
      const list = Array.from(nodes).filter((n) => n.offsetParent !== null);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.removeAttribute("data-modal-open");
      if (opener?.focus) opener.focus();
    };
  }, [open, busy, handleClose]);

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Please enter your name.";
    if (!form.phone.trim()) errs.phone = "Please enter a valid phone number, including the country code.";
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.consent) errs.consent = "Please agree to the use of your details for this request.";
    return errs;
  };

  const submit = async () => {
    if (busy || done) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = ["full_name", "phone", "email", "consent"].find((k) => errs[k]);
      const map = { consent: "consent" };
      document.getElementById(`sr-${map[first] || first}`)?.focus();
      return;
    }
    setBusy(true);
    setSubmitError("");
    if (form.website) { setDone(true); setBusy(false); return; }
    try {
      const idempotencyKey = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      await api.post("/sample-report/requests", {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        consent: true,
        idempotency_key: idempotencyKey,
        attribution: { page_path: window.location.pathname, cta_location: "sample_page" },
      });
      setDone(true);
      trackEvent("sample_request_saved", { source: "sample_page" });
    } catch (e) {
      setSubmitError(formatApiError(e.response?.data?.detail) || "We could not process your request. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[200] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="sample-report-dialog" role="dialog" aria-modal="true" aria-labelledby="sr-title" aria-describedby="sr-desc">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => !busy && handleClose()} data-testid="sample-report-backdrop" />
          <motion.div ref={panelRef} initial={{ y: 24, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 16, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md bg-white rounded-[18px] border border-line max-h-[min(92dvh,100%)] overflow-y-auto overscroll-contain shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-line px-6 py-4 flex items-center justify-between">
              <div>
                <div className="eyebrow mb-1" id="sr-desc">{done ? "Request received" : "Get your free sample report"}</div>
                <div className="font-display text-lg" id="sr-title">Email Me the Free Sample Report</div>
              </div>
              <button onClick={handleClose} data-testid="sample-report-close" aria-label="Close" className="h-11 w-11 grid place-items-center border border-line rounded-md hover:border-ink transition-colors"><X size={18} /></button>
            </div>

            <div className="p-6 lg:p-8">
              {done ? (
                <div className="text-center py-6" data-testid="sample-report-success">
                  <div className="w-16 h-16 rounded-full bg-blue-50 text-blue grid place-items-center mx-auto mb-5"><CheckCircle2 size={32} /></div>
                  <h3 className="text-2xl mb-3">Thank you — your request is received</h3>
                  <p className="text-muted max-w-[44ch] mx-auto mb-3">We have received your request and will email the sample report to <b>{form.email}</b> shortly.</p>
                  <p className="text-[0.8rem] text-muted max-w-[44ch] mx-auto mb-6">Please check your inbox and spam folder. If you don’t see it, reply to this email address and we’ll send it again.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button onClick={() => { setDone(false); setForm(EMPTY); }} className="text-sm text-blue font-semibold hover:underline">Use a different email address</button>
                    <button onClick={handleClose} className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3 rounded-[10px] font-semibold hover:bg-blue-600 transition">Done</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted">Enter your details below and we’ll email you a sample Fedari inspection report as a PDF attachment.</p>
                  {/* Honeypot */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
                    <label htmlFor="sr-website">Website</label>
                    <input id="sr-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} />
                  </div>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[0.76rem] font-semibold text-ink">Full name *</span>
                    <input id="sr-full-name" name="full_name" autoComplete="name" required aria-invalid={!!errors.full_name} aria-describedby={errors.full_name ? "sr-full-name-err" : undefined} className="w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-ink focus:border-blue focus:ring-2 focus:ring-blue/15 outline-none transition" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your full name" data-testid="sample-report-name" />
                    {errors.full_name && <p id="sr-full-name-err" className="text-xs text-sev-high">{errors.full_name}</p>}
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[0.76rem] font-semibold text-ink">Phone number *</span>
                    <input id="sr-phone" name="phone" type="tel" autoComplete="tel" required aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "sr-phone-err" : undefined} className="w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-ink focus:border-blue focus:ring-2 focus:ring-blue/15 outline-none transition" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+971 50 000 0000" data-testid="sample-report-phone" />
                    {errors.phone && <p id="sr-phone-err" className="text-xs text-sev-high">{errors.phone}</p>}
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[0.76rem] font-semibold text-ink">Email address *</span>
                    <input id="sr-email" name="email" type="email" autoComplete="email" required aria-invalid={!!errors.email} aria-describedby={errors.email ? "sr-email-err" : undefined} className="w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-ink focus:border-blue focus:ring-2 focus:ring-blue/15 outline-none transition" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="sample-report-email" />
                    {errors.email && <p id="sr-email-err" className="text-xs text-sev-high">{errors.email}</p>}
                  </label>
                  <label htmlFor="sr-consent" className="flex items-start gap-2.5 text-sm text-bodytext min-h-[44px] py-1">
                    <input id="sr-consent" name="consent" type="checkbox" required checked={form.consent} onChange={(e) => set("consent", e.target.checked)} aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "sr-consent-err" : undefined} data-testid="sample-report-consent" className="h-5 w-5 mt-0.5 accent-blue" />
                    <span>I agree that Fedari may use my name, phone number and email address to process my sample report request, as explained in the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue font-semibold underline">Privacy Policy</a>.</span>
                  </label>
                  {errors.consent && <p id="sr-consent-err" className="text-xs text-sev-high -mt-2">{errors.consent}</p>}

                  {submitError && (
                    <div className="flex items-start gap-2.5 bg-[#FDECEC] border border-[#F8C6C9] text-[#C81E2B] rounded-lg px-4 py-3 text-sm" role="alert" data-testid="sample-report-error">
                      <AlertTriangle size={17} className="flex-none mt-0.5" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <button onClick={submit} disabled={busy} data-testid="sample-report-submit" className="w-full bg-blue text-white py-3.5 rounded-[10px] font-semibold flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors disabled:opacity-60">
                    {busy ? <><Loader2 className="animate-spin" size={18} /> Sending your request…</> : "Email Me the Free Sample Report"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
